import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ReadingRecord {
  date: string
  count: number
  bookTitle?: string
}

interface ReadingCalendarProps {
  records: ReadingRecord[]
  onDateSelect?: (date: string) => void
  selectedDate?: string
}

export default function ReadingCalendar({ 
  records, 
  onDateSelect, 
  selectedDate 
}: ReadingCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())

  const { year, month } = useMemo(() => ({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth(),
  }), [currentDate])

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ]

  const weekDays = ['日', '一', '二', '三', '四', '五', '六']

  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)
  const firstDayWeekday = firstDayOfMonth.getDay()
  const daysInMonth = lastDayOfMonth.getDate()

  const recordsMap = useMemo(() => {
    const map = new Map<string, ReadingRecord>()
    records.forEach(record => {
      map.set(record.date, record)
    })
    return map
  }, [records])

  const getIntensityClass = (count: number) => {
    if (count === 0) return 'bg-gray-100'
    if (count < 10) return 'bg-gray-200'
    if (count < 20) return 'bg-green-100'
    if (count < 30) return 'bg-green-200'
    if (count < 40) return 'bg-green-300'
    if (count < 50) return 'bg-green-400'
    if (count < 60) return 'bg-green-500'
    if (count < 70) return 'bg-green-600'
    if (count < 80) return 'bg-green-700'
    if (count < 90) return 'bg-green-800'
    if (count < 100) return 'bg-green-900'
    return 'bg-emerald-900'
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev)
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
  }

  const handleDateClick = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    onDateSelect?.(dateStr)
  }

  const renderCalendarDays = () => {
    const days = []
    
    // 空白天数（上个月的尾部）
    for (let i = 0; i < firstDayWeekday; i++) {
      days.push(<div key={`empty-${i}`} className="h-8" />)
    }

    // 当月的天数
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      const record = recordsMap.get(dateStr)
      const count = record?.count || 0
      const isSelected = selectedDate === dateStr
      const isToday = dateStr === new Date().toISOString().split('T')[0]

      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(day)}
          className={cn(
            'h-8 w-8 rounded-md flex items-center justify-center text-xs font-medium cursor-pointer transition-all hover:scale-110',
            getIntensityClass(count),
            isSelected && 'ring-2 ring-primary-500 ring-offset-1',
            isToday && 'ring-1 ring-blue-400',
            count > 0 ? 'text-white' : 'text-gray-700'
          )}
          title={record ? `${record.date}: ${count}遍${record.bookTitle ? ` - ${record.bookTitle}` : ''}` : `${dateStr}: 无记录`}
        >
          {day}
        </div>
      )
    }

    return days
  }

  return (
    <div className="bg-white rounded-lg border p-4">
      {/* 头部导航 */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <h3 className="text-lg font-semibold text-gray-900">
          {year}年 {monthNames[month]}
        </h3>

        <button
          onClick={() => navigateMonth('next')}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* 星期标题 */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      {/* 日历网格 */}
      <div className="grid grid-cols-7 gap-1">
        {renderCalendarDays()}
      </div>

      {/* 图例 */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <BookOpen className="h-3 w-3" />
          <span>阅读强度</span>
        </div>
        <div className="flex items-center space-x-1">
          <span className="text-xs text-gray-500">0</span>
          <div className="h-3 w-3 bg-gray-100 rounded-sm" />
          <div className="h-3 w-3 bg-green-100 rounded-sm" />
          <div className="h-3 w-3 bg-green-200 rounded-sm" />
          <div className="h-3 w-3 bg-green-300 rounded-sm" />
          <div className="h-3 w-3 bg-green-400 rounded-sm" />
          <div className="h-3 w-3 bg-green-500 rounded-sm" />
          <div className="h-3 w-3 bg-green-600 rounded-sm" />
          <div className="h-3 w-3 bg-green-700 rounded-sm" />
          <div className="h-3 w-3 bg-green-800 rounded-sm" />
          <div className="h-3 w-3 bg-green-900 rounded-sm" />
          <span className="text-xs text-gray-500">100+</span>
        </div>
      </div>
    </div>
  )
}
